/**
 * KPIs acionáveis do Cadastro de Suprimentos (redesign 2026-09-15). Mesma
 * anatomia de `RequisicoesCompra/KpisFila`: classes canônicas
 * `.kpi-strip-v2`, borda superior na cor do indicador, cada cartão é um
 * botão que liga/desliga o filtro rápido correspondente (`filtros.RAPIDOS`).
 *
 * "Valor em estoque" não filtra (é um agregado). "Sem giro 90 d" lê o
 * consumo de 90 d servido na lista (ADR-0070).
 */
import React from "react";

import useMediaQuery from "../../../hooks/useMediaQuery";
import { mq } from "../../../styles/canonical/breakpoints";
import { formatarReaisBR } from "../../../utils/dinheiro";

function fmtMil(valor) {
  const n = Number(valor || 0);
  if (n >= 1000) return { valor: `R$ ${(n / 1000).toLocaleString("pt-BR", { maximumFractionDigits: 1 })}`, unidade: "mil" };
  return { valor: formatarReaisBR(n), unidade: "" };
}

function pct(parte, total) {
  if (!total) return "0%";
  return `${Math.round((parte / total) * 100)}%`;
}

/**
 * `resumo`: { ativos, inativos, abaixo, valorRepor, semPreco, semFornecedor,
 * valorEstoque, depositos, semGiro, valorParado }.
 */
export default function KpisCadastro({ resumo, ativo, onFiltrar }) {
  // Celular (auditoria mobile 2026-09-24): sem colunas inline — a faixa
  // rolável de uma linha do CSS assume — e só número + rótulo; a legenda
  // truncada ("11 % dos ativos · orça…") escondia justamente a explicação.
  const mobile = useMediaQuery(mq.mobile);
  const r = resumo || {};
  const emEstoque = fmtMil(r.valorEstoque);
  const parado = fmtMil(r.valorParado);

  const cards = [
    {
      id: "ativos", label: "Total ativos", valor: r.ativos ?? "—", cor: "var(--neutral-solid)",
      sub: r.inativos ? `${r.inativos} inativo${r.inativos === 1 ? "" : "s"} no catálogo` : "nenhum inativo",
    },
    {
      id: "abaixo", label: "Abaixo do mínimo", valor: r.abaixo ?? "—", unidade: "itens", cor: "var(--danger-solid)",
      corValor: r.abaixo > 0 ? "var(--danger-text)" : undefined,
      sub: r.abaixo > 0 ? `≈ ${fmtMil(r.valorRepor).valor} ${fmtMil(r.valorRepor).unidade} para repor`.trim() : "tudo acima do mínimo",
    },
    {
      id: "sem_preco", label: "Sem preço", valor: r.semPreco ?? "—", unidade: "itens", cor: "var(--warning-solid)",
      sub: `${pct(r.semPreco, r.ativos)} dos ativos · orçam a R$ 0`,
    },
    {
      id: "sem_fornecedor", label: "Sem fornecedor", valor: r.semFornecedor ?? "—", unidade: "itens", cor: "var(--warning-solid)",
      sub: "não entram na cotação",
    },
    {
      id: "valor", label: "Valor em estoque", valor: emEstoque.valor, unidade: emEstoque.unidade, cor: "var(--accent)",
      sub: `${r.depositos ? `${r.depositos} depósito${r.depositos === 1 ? "" : "s"} · ` : ""}saldo × custo médio`, semFiltro: true,
    },
    {
      id: "sem_giro", label: "Sem giro 90 d", valor: r.semGiro ?? "—", unidade: "itens", cor: "var(--teal-solid)",
      sub: r.semGiro > 0 ? `${parado.valor} ${parado.unidade} parados`.trim() : "tudo girou nos últimos 90 d",
    },
  ];

  return (
    <div className="kpi-strip-v2" style={{ ...(mobile ? {} : { gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))" }), marginBottom: 0 }}>
      {cards.map((c) => {
        const clicavel = !c.semFiltro && Boolean(onFiltrar);
        const on = ativo === c.id;
        return (
          <div
            key={c.id}
            className="kpi-v2"
            data-testid={`kpi-${c.id}`}
            role={clicavel ? "button" : "status"}
            tabIndex={clicavel ? 0 : undefined}
            onClick={clicavel ? () => onFiltrar(on ? null : c.id) : undefined}
            onKeyDown={clicavel ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onFiltrar(on ? null : c.id); } } : undefined}
            aria-pressed={clicavel ? on : undefined}
            style={{
              borderTop: `2px solid ${c.cor}`,
              padding: "12px 16px 13px",
              gap: 3,
              cursor: clicavel ? "pointer" : "default",
              background: on ? "var(--accent-soft)" : undefined,
              transition: "background .15s ease",
            }}
            title={clicavel ? (on ? "Clique para tirar o filtro" : "Clique para filtrar") : undefined}
          >
            <div className="lbl" style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", fontFamily: "var(--font-body)", fontWeight: 600 }}>{c.label}</div>
            <div className="val" style={{ fontSize: 22, fontWeight: 700, lineHeight: 1.15, color: c.corValor || undefined }}>
              {c.valor}
              {c.unidade ? <span className="unit">{c.unidade}</span> : null}
            </div>
            {mobile ? null : (
              <div className="meta" style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", fontSize: 12 }}>
                <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{c.sub}</span>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
